import { computed, isRef, ref, unref, watch } from 'vue'
import type { Ref } from 'vue'
import type { KarteUserListKarteFragment } from '../KarteUserList/KarteUserList.generated'
import { useFetchKartenForKarteListPageLazyQuery } from './useKartenList.generated'

type ArgsType = Parameters<typeof useFetchKartenForKarteListPageLazyQuery>
type ArgVariable = ArgsType[0]
type ArgOption = ArgsType[1]

export const useLazyKartenList = (
  variables: ArgVariable,
  options: ArgOption
) => {
  const {
    result,
    refetch,
    fetchMore: fetchMore_,
    onResult,
    onError,
    load,
  } = useFetchKartenForKarteListPageLazyQuery(variables, options)

  // 各検索条件の初回読み込み中かどうかを管理する
  // fetchMore での読み込み時は更新されない
  //
  // マウントされたら初回読み込み中であるものとして初期値 true にしている
  const initialLoading = ref(true)

  // initialLoading と karten の状態変更のタイミングを合わせたいので、ref にして任意のタイミングで更新できるようにする
  // 具体的には onResult で karten を更新した後に initialLoading を false にする
  const karten: Ref<KarteUserListKarteFragment[]> = ref([])

  // variables の変更、つまり検索条件の変更時にリストをローディング状態の見た目にするために、initialLoading を true にする
  //
  // 実装時に思っていたこと： この watch で cache.evict({ fieldName: 'karten' }) すると karten も computed になって良さそうだが、微妙に UI としての empty 状態と loading 状態の表示が高速でちらついたりするのでこのようにしている
  // わりと FIXME
  const computedVariables = computed(() => {
    // variables は undefined の可能性があるので watch に直接渡せない
    // そのため、computed でラップしている
    return isRef(variables) ? variables.value : variables
  })
  watch(computedVariables, () => {
    initialLoading.value = true
  })

  const hasNext = computed(() => result.value?.karten.pageInfo.hasNextPage)

  const onCompleted = () => {
    initialLoading.value = false
  }

  onResult(params => {
    const setResult = () => {
      if (params.loading || !params.data?.karten) {
        return
      }

      const ks = params.data.karten.edges.map(({ node }) => node)
      if (ks.length === 0) {
        karten.value = []
      }

      const variablesValue = (() => {
        const v = unref(variables)

        return typeof v === 'function' ? v() : v
      })()

      // アプローチしたときに、カルテ一覧をフェッチしなおしていないので、キャッシュのうち、アプローチしたかどうかをフィルタする必要がある
      // アプローチ時、アプローチしたカルテ単体のキャッシュは更新されている
      const filteredByHasAlreadyBeenCommentedByMe = (() => {
        if (variablesValue?.filter?.hasAlreadyBeenCommentedByMe == null) {
          return ks
        }
        return ks.filter(k => {
          if (variablesValue.filter?.hasAlreadyBeenCommentedByMe) {
            return !!k.ownKartePlannerComment
          } else {
            return !k.ownKartePlannerComment
          }
        })
      })()

      // 重複排除する
      const karteMap = Object.fromEntries(
        filteredByHasAlreadyBeenCommentedByMe.map(karte => [karte.id, karte])
      )
      const dedupedKarteIdList = Array.from(
        new Set(filteredByHasAlreadyBeenCommentedByMe.map(karte => karte.id))
      )
      karten.value = dedupedKarteIdList.map(karteId => karteMap[karteId])
    }

    setResult()
    onCompleted()
  })

  onError(_ => onCompleted())

  const fetchMore = (): ReturnType<typeof fetchMore_> => {
    return fetchMore_({
      variables: { after: result.value?.karten.pageInfo.endCursor },
    })
  }

  return { karten, initialLoading, refetch, fetchMore, hasNext, load }
}
